import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Network, TrendingUp, AlertTriangle, Globe } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const AirFreightArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.backToHighlights')}
          </Button>
          <LanguageSwitcher />
        </div>

        <article className="max-w-4xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Badge className="bg-blue-500 text-white">
                <Network className="h-3 w-3 mr-1" />
                {t('article.airfreight.badge')}
              </Badge>
              <span className="text-gray-500 text-sm">{t('article.airfreight.date')}</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('article.airfreight.title')}
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('article.airfreight.summary')}
            </p>
          </header>

          <div className="mb-8">
            <img 
              src="https://www.maersk.com/~/media_sc9/maersk/insights/resilience/2025/12/man-and-woman-in-front-of-monitor_1024x576.jpg?w=1056&hash=C6F088BF1E7051DAC02E71ACDD7C6CAA"
              alt="Allegro international e-commerce expansion" 
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2 text-red-600" />
                  {t('airfreight.stats.capacity.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-red-600">{t('airfreight.stats.capacity.value')}</div>
                <p className="text-sm text-gray-600">{t('airfreight.stats.capacity.desc')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2 text-orange-600" />
                  {t('airfreight.stats.rate.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-orange-600">{t('airfreight.stats.rate.value')}</div>
                <p className="text-sm text-gray-600">{t('airfreight.stats.rate.desc')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Globe className="h-5 w-5 mr-2 text-blue-600" />
                  {t('airfreight.stats.season.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-blue-600">{t('airfreight.stats.season.value')}</div>
                <p className="text-sm text-gray-600">{t('airfreight.stats.season.desc')}</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose max-w-none">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('airfreight.section1.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('airfreight.section1.content')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('airfreight.section2.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('airfreight.section2.content')}
            </p>
            <div className="whitespace-pre-line text-gray-700 mb-6 pl-6">
              {t('airfreight.section2.hubs')}
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('airfreight.section3.title')}</h3>
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Northeast Asia:</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('airfreight.section3.northeast')}
              </div>
            </div>

            <div className="bg-orange-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Southeast Asia Strain:</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('airfreight.section3.southeast')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('airfreight.section4.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('airfreight.section4.content')}
            </p>

            <div className="bg-green-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Source Information:</h4>
              <p className="text-sm text-gray-600 mb-2">
                {t('airfreight.source')}
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-blue-600 border-blue-200"
                onClick={() => window.open('https://www.maersk.com/insights/resilience/2025/12/22/tech-supply-chain-resilience-collaboration', '_blank')}
              >
                View Original Source
              </Button>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default AirFreightArticle;